import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
} from "@mui/material";
import {
  addDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { propertyCollection } from "../utils/firestorePaths";
import {
  calculateWarrantyEndDate,
  convertToMonths,
  formatDateISO,
  getLocalDateString,
  isSerialNumberUnique,
  validateWarrantyForm,
} from "../utils/warrantyUtils";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: "90vh",
  overflow: "auto",
};

const AddWarrantyModal = ({
  open,
  onClose,
  clientId,
  selectedProperty,
  userInfo,
  editWarranty = null,
  existingWarranties = [],
}) => {
  // Form state
  const [items, setItems] = useState([]);
  const [warrantyTypes, setWarrantyTypes] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [serialNumber, setSerialNumber] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(getLocalDateString());
  const [purchaseCost, setPurchaseCost] = useState("");
  const [warrantyMode, setWarrantyMode] = useState("template"); // 'template' or 'custom'
  const [selectedWarrantyType, setSelectedWarrantyType] = useState(null);
  const [customDuration, setCustomDuration] = useState(1);
  const [customDurationUnit, setCustomDurationUnit] = useState("months");
  const [warrantyStartDate, setWarrantyStartDate] = useState(getLocalDateString());
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Load items from Firestore
  useEffect(() => {
    if (!selectedProperty) return;

    const itemsRef = propertyCollection(clientId, selectedProperty, "items");
    const q = query(itemsRef, orderBy("itemName"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const itemsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setItems(itemsData);
    });

    return () => unsubscribe();
  }, [selectedProperty]);

  // Load warranty types from Firestore
  useEffect(() => {
    if (!selectedProperty) return;

    const warrantyTypesRef = propertyCollection(
      clientId,
      selectedProperty,
      "warrantyTypes"
    );
    const q = query(warrantyTypesRef, orderBy("name"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const typesData = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .filter((type) => type.isActive); // Only show active types
      setWarrantyTypes(typesData);
    });

    return () => unsubscribe();
  }, [selectedProperty]);

  // Populate form when editing
  useEffect(() => {
    if (editWarranty) {
      setSerialNumber(editWarranty.serialNumber || "");
      setPoNumber(editWarranty.poNumber || "");
      setPurchaseDate(editWarranty.purchaseDate || getLocalDateString());
      setPurchaseCost(editWarranty.purchaseCost || "");
      setWarrantyStartDate(editWarranty.warrantyStartDate || getLocalDateString());
      setLocation(editWarranty.location || "");
      setNotes(editWarranty.notes || "");

      // Set item
      const item = items.find((i) => i.id === editWarranty.itemId);
      if (item) setSelectedItem(item);

      // Set warranty type or custom
      if (editWarranty.warrantyTypeId) {
        setWarrantyMode("template");
        const type = warrantyTypes.find((t) => t.id === editWarranty.warrantyTypeId);
        if (type) setSelectedWarrantyType(type);
      } else if (editWarranty.customDurationMonths) {
        setWarrantyMode("custom");
        setCustomDuration(editWarranty.customDurationMonths);
        setCustomDurationUnit("months");
      }
    } else {
      // Reset form for new warranty
      resetForm();
    }
  }, [editWarranty, items, warrantyTypes]);

  // Auto-fill vendor, type, cost when item selected
  useEffect(() => {
    if (selectedItem && !editWarranty) {
      setPurchaseCost(selectedItem.itemCost || "");
    }
  }, [selectedItem, editWarranty]);

  // Sync warranty start date with purchase date by default
  useEffect(() => {
    if (!editWarranty) {
      setWarrantyStartDate(purchaseDate);
    }
  }, [purchaseDate, editWarranty]);

  const resetForm = () => {
    setSelectedItem(null);
    setSerialNumber("");
    setPoNumber("");
    setPurchaseDate(getLocalDateString());
    setPurchaseCost("");
    setWarrantyMode("template");
    setSelectedWarrantyType(null);
    setCustomDuration(1);
    setCustomDurationUnit("months");
    setWarrantyStartDate(getLocalDateString());
    setLocation("");
    setNotes("");
    setError("");
  };

  const handleSave = async () => {
    setError("");

    // Validate form
    const formData = {
      selectedItem,
      serialNumber,
      purchaseDate,
      warrantyMode,
      selectedWarrantyType,
      customDuration,
      customDurationUnit,
    };

    const validationError = validateWarrantyForm(formData);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Validate serial number uniqueness
    if (
      !isSerialNumberUnique(serialNumber, existingWarranties, editWarranty?.id)
    ) {
      setError("This serial number is already registered in the system");
      return;
    }

    setLoading(true);

    try {
      // Calculate warranty end date
      let durationMonths;
      let warrantyTypeName = "";
      let warrantyTypeId = "";

      if (warrantyMode === "template") {
        durationMonths = selectedWarrantyType.durationMonths;
        warrantyTypeName = selectedWarrantyType.name;
        warrantyTypeId = selectedWarrantyType.id;
      } else {
        durationMonths = convertToMonths(customDuration, customDurationUnit);
      }

      const endDate = calculateWarrantyEndDate(warrantyStartDate, durationMonths);

      const warrantyData = {
        // Item linkage
        itemId: selectedItem.id,
        itemName: selectedItem.itemName,
        itemType: selectedItem.itemType,
        vendor: selectedItem.vendor || "",

        // Serial number & identification
        serialNumber: serialNumber.trim(),

        // Purchase tracking
        poNumber: poNumber.trim(),
        purchaseDate: purchaseDate,
        purchaseCost: parseFloat(purchaseCost) || 0,

        // Warranty coverage
        warrantyTypeId: warrantyTypeId,
        warrantyTypeName: warrantyTypeName,
        customDurationMonths: warrantyMode === "custom" ? durationMonths : null,
        warrantyStartDate: warrantyStartDate,
        warrantyEndDate: endDate,

        // Status
        status: "Active",

        // Notes & tracking
        notes: notes.trim(),
        location: location.trim(),

        // Audit
        lastModified: serverTimestamp(),
        modifiedBy: userInfo?.id || "unknown",
      };

      const warrantiesRef = propertyCollection(
        clientId,
        selectedProperty,
        "warranties"
      );

      if (editWarranty && editWarranty.id) {
        // Update existing warranty
        const warrantyDoc = doc(warrantiesRef, editWarranty.id);
        await updateDoc(warrantyDoc, warrantyData);
      } else {
        // Create new warranty
        await addDoc(warrantiesRef, {
          ...warrantyData,
          createdAt: serverTimestamp(),
          createdBy: userInfo?.id || "unknown",
        });
      }

      // Success - close modal
      resetForm();
      onClose();
    } catch (err) {
      console.error("Error saving warranty:", err);
      setError("Failed to save warranty. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {editWarranty ? "Edit Warranty" : "Add Warranty Item"}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Autocomplete
          value={selectedItem}
          onChange={(event, newValue) => setSelectedItem(newValue)}
          options={items}
          getOptionLabel={(option) => option.itemName || ""}
          renderInput={(params) => (
            <TextField {...params} label="Item *" placeholder="Select an item" />
          )}
          sx={{ mb: 2 }}
        />

        {selectedItem && (
          <Box sx={{ mb: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Type:</strong> {selectedItem.itemType}
            </Typography>
            <Typography variant="body2">
              <strong>Vendor:</strong> {selectedItem.vendor || "N/A"}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          label="Serial Number *"
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
          placeholder="Enter serial number"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="PO Number"
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
          placeholder="Purchase order reference"
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            label="Purchase Date *"
            type="date"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1 }}
          />

          <TextField
            label="Purchase Cost"
            type="number"
            value={purchaseCost}
            onChange={(e) => setPurchaseCost(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ flex: 1 }}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Warranty Duration *
        </Typography>

        <ToggleButtonGroup
          value={warrantyMode}
          exclusive
          onChange={(e, newMode) => newMode && setWarrantyMode(newMode)}
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="template">Use Template</ToggleButton>
          <ToggleButton value="custom">Custom Duration</ToggleButton>
        </ToggleButtonGroup>

        {warrantyMode === "template" ? (
          <Autocomplete
            value={selectedWarrantyType}
            onChange={(event, newValue) => setSelectedWarrantyType(newValue)}
            options={warrantyTypes}
            getOptionLabel={(option) => option.name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Warranty Type *"
                placeholder="Select warranty template"
              />
            )}
            sx={{ mb: 2 }}
          />
        ) : (
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Duration *"
              type="number"
              value={customDuration}
              onChange={(e) => setCustomDuration(Number(e.target.value))}
              inputProps={{ min: 1 }}
              sx={{ flex: 1 }}
            />

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Unit *</InputLabel>
              <Select
                value={customDurationUnit}
                label="Unit *"
                onChange={(e) => setCustomDurationUnit(e.target.value)}
              >
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="months">Months</MenuItem>
                <MenuItem value="years">Years</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        <TextField
          fullWidth
          label="Warranty Start Date"
          type="date"
          value={warrantyStartDate}
          onChange={(e) => setWarrantyStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Defaults to purchase date, but can be overridden"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Location / Room"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g., Room 101, Kitchen"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
          placeholder="Additional notes about this warranty"
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : editWarranty ? "Update" : "Add Warranty"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddWarrantyModal;
